// @flow

import moment from 'moment';
import { has, get, isEmpty } from 'lodash';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { momentFromApiDate } from 'Utils/date/apiDate';
import { momentFromUTCDateTime } from 'Utils/date/dateUtils';
import {
  API_AVAILABLE,
  API_ACTIVATED,
  API_VALIDATED,
  API_UNAVAILABLE,
  API_RESORT_ONLY,
} from 'Utils/api-status/constants';

type DurationUnit = 'day' | 'hour' | 'minute';

type DurationDiff = {
  value: number,
  unit: DurationUnit,
};

function pluralizeLabel(singularLabel: string, pluralLabel: string, amount: number): string {
  return amount > 1 ? pluralLabel : singularLabel;
}

function getDateDiff(startDate: string, endDate?: string): number {
  if (!startDate) {
    return 0;
  }
  const startMoment = momentFromApiDate(startDate);
  const endMoment = endDate ? momentFromApiDate(endDate) : moment();
  return endMoment.diff(startMoment, 'days');
}

function getDiffResult(duration: number, type: DurationUnit): 0 | DurationDiff {
  const result = Math.floor(Math.abs(duration));
  return (
    result && {
      value: result,
      unit: type,
    }
  );
}

function getDuration(startDate: string, endDate: ?string): moment$MomentDuration {
  const startMoment = momentFromUTCDateTime(startDate);
  const endMoment = endDate ? momentFromUTCDateTime(endDate) : moment();
  return moment.duration(endMoment.diff(startMoment));
}

function getDurationDiff(startDate: string, endDate: ?string): DurationDiff {
  const duration = getDuration(startDate, endDate);
  const defaultDiff = {
    value: 1,
    unit: 'minute',
  };

  const durationDays = getDiffResult(duration.asDays(), 'day');
  const diffDays = durationDays && durationDays.value > 2 ? durationDays : 0;

  return (
    diffDays ||
    getDiffResult(duration.asHours(), 'hour') ||
    getDiffResult(duration.asMinutes(), 'minute') ||
    defaultDiff
  );
}

type EasyArrivalConfig = {
  soonText: string,
  availableText: string,
  availableLinkLabel: string,
  tooLateLinkLabel: string,
  tooLateText: string,
  servicesFullyBookedText: string,
  subtitle?: string,
};

type ReturnArrivalInfos = {
  NOT_YET: {
    text: string,
  },
  AVAILABLE: {
    text: string,
    linkLabel: string,
  },
  UNEDITABLE: {
    text: string,
    linkLabel: string,
  },
  MAX_QUOTA_REACHED: {
    text: string,
    linkLabel: string,
  },
};

function getEasyArrivalInfos(
  easyArrivalConfig: EasyArrivalConfig,
  status: string,
): ?ReturnArrivalInfos {
  const easyArrivalInfos = {
    NOT_YET: {
      text: easyArrivalConfig.soonText,
    },
    AVAILABLE: {
      text: easyArrivalConfig.availableText,
      linkLabel: easyArrivalConfig.availableLinkLabel,
    },
    UNEDITABLE: {
      text: easyArrivalConfig.tooLateText,
      linkLabel: easyArrivalConfig.tooLateLinkLabel,
    },
    MAX_QUOTA_REACHED: {
      text: easyArrivalConfig.servicesFullyBookedText,
      linkLabel: easyArrivalConfig.availableLinkLabel,
    },
  };

  return has(easyArrivalInfos, status) ? easyArrivalInfos[status] : null;
}

function checkPropertiesForEasyArrival(easyArrival: *, config: CMSContent): boolean {
  const easyArrivalConfig = get(config, 'memberSpace.easyArrival', null);
  const easyArrivalBooking = get(easyArrival, 'status');

  return (
    !isEmpty(easyArrivalConfig) &&
    (easyArrivalBooking !== API_UNAVAILABLE &&
      easyArrivalBooking !== 'UNAVAILABLE_FOR_THIS_COUNTRY')
  );
}

type EasyArrivalProps = {
  headerLabel: string,
  image: string,
  texts: Array<string>,
  link: string,
  linkLabel: string,
};

function getPropsForEasyArrival(
  easyArrival: *,
  easyArrivalConfig: EasyArrivalConfig,
  toDisplay: boolean = true,
): ?EasyArrivalProps {
  const easyArrivalInfos =
    toDisplay && !isEmpty(easyArrivalConfig)
      ? getEasyArrivalInfos(easyArrivalConfig, get(easyArrival, 'status'))
      : null;

  return (
    easyArrivalInfos && {
      headerLabel: get(easyArrivalConfig, 'title'),
      image: get(easyArrivalConfig, 'image'),
      texts: [get(easyArrivalConfig, 'subtitle'), get(easyArrivalInfos, 'text')],
      link: get(easyArrival, 'href'),
      linkLabel: get(easyArrivalInfos, 'linkLabel'),
    }
  );
}

type AppDownloadProps = {
  title: ?string,
  description: ?string,
  buttonLabel: ?string,
  url: ?string,
};
function getPropsForAppDownload(stay: Stay, cmsContent: CustomerDashboardI18n): AppDownloadProps {
  const url = get(stay, 'appUrl');

  return {
    title: get(cmsContent, 'memberSpace.prepareMyStay.downloadResortsApp.dashboardBlock.title'),
    description: get(
      cmsContent,
      'memberSpace.prepareMyStay.downloadResortsApp.dashboardBlock.description',
    ),
    buttonLabel: get(
      cmsContent,
      'memberSpace.prepareMyStay.downloadResortsApp.dashboardBlock.downloadButtonLabel',
    ),
    url,
  };
}

function checkPropertiesForAppDownload(stay: *, cmsContent: CustomerDashboardI18n): boolean {
  const url = get(stay, 'appUrl');
  const displayDownload = get(
    cmsContent,
    'memberSpace.prepareMyStay.downloadResortsApp.displayDownload',
  );
  const difference = getDateDiff(get(stay, 'departureDate')) * -1;

  return !!(
    stay &&
    stay.resort &&
    stay.resort.hasMobileApp &&
    url &&
    displayDownload &&
    difference < 35
  );
}

const checkPropertiesForPickMyRoom = (appContext: AppContext) => {
  const getFeatureFlipValue = isFeatureFlipped(appContext);
  return (status: string): boolean => {
    const statuses = [API_AVAILABLE, API_VALIDATED, API_ACTIVATED];

    if (getFeatureFlipValue('pickYourRoom.resortOnlyRequestForm')) {
      statuses.push(API_RESORT_ONLY);
    }

    return getFeatureFlipValue('pickYourRoom.allow') && statuses.includes(status);
  };
};

function isPickMyRoomChecked(status: string): boolean {
  return [API_VALIDATED, API_ACTIVATED].includes(status);
}

export {
  checkPropertiesForAppDownload,
  checkPropertiesForEasyArrival,
  checkPropertiesForPickMyRoom,
  isPickMyRoomChecked,
  getEasyArrivalInfos,
  getDateDiff,
  getDurationDiff,
  getPropsForEasyArrival,
  getPropsForAppDownload,
  pluralizeLabel,
};
